// Maps a storefront pathname to one of the fixed page_type values used in GTM triggers/variables.
export const getPageType = (pathname) => {
  if (pathname === '/') return 'home';
  if (pathname === '/products') return 'search';
  if (pathname === '/discounts') return 'discount';
  if (pathname === '/checkout') return 'checkout';
  if (pathname.startsWith('/product/')) return 'product';
  if (pathname.startsWith('/subcategory/')) return 'sub_category';
  if (pathname.startsWith('/category/')) return 'category';
  return 'other';
};

// Pushes a page_view event to the GTM data layer set up in public/index.html.
export const pushPageView = (path, pageType) => {
  if (typeof window === 'undefined' || !window.dataLayer) return;

  window.dataLayer.push({
    event: 'page_view',
    page_path: path,
    page_type: pageType || getPageType(path),
    page_title: document.title,
    page_location: window.location.href,
  });
};

// Pushes a GA4 view_item_list event for the products currently shown on the
// all-products, category, or sub-category page. `page`/`pageSize` are used to
// compute each item's absolute position across pagination.
export const pushViewItemList = (products, { listId, listName, page = 1, pageSize }) => {
  if (typeof window === 'undefined' || !window.dataLayer) return;
  if (!Array.isArray(products) || products.length === 0) return;

  const size = pageSize || products.length;

  // Clear the previous ecommerce object so it doesn't merge into this one.
  window.dataLayer.push({ ecommerce: null });
  window.dataLayer.push({
    event: 'view_item_list',
    ecommerce: {
      item_list_id: listId,
      item_list_name: listName,
      items: products.map((p, i) => ({
        item_id: p.id,
        item_name: p.name,
        item_category: p.category || '',
        item_category2: p.subCategory || '',
        item_list_id: listId,
        item_list_name: listName,
        index: (page - 1) * size + i,
        price: p.price,
        quantity: 1,
      })),
    },
  });
};

const CURRENCY = 'BDT';

// The discount amount GA4 expects on an ecommerce item: original price minus
// the current price, or 0 when there's no markdown.
export const getItemDiscount = (item) => {
  const original = item.originalPrice;
  const price = item.price;
  return original && original > price ? original - price : 0;
};

// Pushes a GA4 view_item event on product detail page load.
export const pushViewItem = (product, price) => {
  if (typeof window === 'undefined' || !window.dataLayer) return;
  if (!product) return;

  window.dataLayer.push({ ecommerce: null });
  window.dataLayer.push({
    event: 'view_item',
    ecommerce: {
      currency: CURRENCY,
      value: price,
      items: [{
        item_id: product.id,
        item_name: product.name,
        discount: getItemDiscount({ originalPrice: product.originalPrice, price }),
        item_category: product.category || '',
        item_category2: product.subCategory || '',
        price,
        quantity: 1,
      }],
    },
  });
};

// Shapes a cart-line item into a GA4 ecommerce item entry.
const mapCartItem = (item) => ({
  item_id: item.id,
  item_name: item.name,
  discount: getItemDiscount(item),
  item_category: item.category || '',
  item_category2: item.subCategory || '',
  item_variant: item.size || '',
  price: item.price,
  quantity: item.quantity,
});

// Pushes a GA4 add_to_cart event — on adding a product to the cart, or
// pressing the + button on an existing cart line.
export const pushAddToCart = (item, quantity) => {
  if (typeof window === 'undefined' || !window.dataLayer) return;
  if (!item) return;

  window.dataLayer.push({ ecommerce: null });
  window.dataLayer.push({
    event: 'add_to_cart',
    ecommerce: {
      currency: CURRENCY,
      value: item.price * quantity,
      items: [mapCartItem({ ...item, quantity })],
    },
  });
};

// Pushes a GA4 view_cart event whenever the customer opens the cart drawer.
export const pushViewCart = (items, totalAmount) => {
  if (typeof window === 'undefined' || !window.dataLayer) return;
  if (!Array.isArray(items) || items.length === 0) return;

  window.dataLayer.push({ ecommerce: null });
  window.dataLayer.push({
    event: 'view_cart',
    ecommerce: {
      currency: CURRENCY,
      value: totalAmount,
      items: items.map(mapCartItem),
    },
  });
};

// Pushes a GA4 remove_from_cart event. `items` should carry the quantity
// actually being removed for each line (1 for a single decrement, the full
// line quantity for a delete, or every cart line for a full cart clear).
export const pushRemoveFromCart = (items, value) => {
  if (typeof window === 'undefined' || !window.dataLayer) return;
  if (!Array.isArray(items) || items.length === 0) return;

  window.dataLayer.push({ ecommerce: null });
  window.dataLayer.push({
    event: 'remove_from_cart',
    ecommerce: {
      currency: CURRENCY,
      value,
      items: items.map(mapCartItem),
    },
  });
};

const getShippingTier = (isDhaka) =>
  isDhaka ? 'Inside Dhaka Standard Delivery' : 'Outside Dhaka Courier';

// Pushes a GA4 add_shipping_info event when a district is selected at checkout.
export const pushAddShippingInfo = (items, totalAmount, isDhaka) => {
  if (typeof window === 'undefined' || !window.dataLayer) return;
  if (!Array.isArray(items) || items.length === 0) return;

  window.dataLayer.push({ ecommerce: null });
  window.dataLayer.push({
    event: 'add_shipping_info',
    ecommerce: {
      currency: CURRENCY,
      value: totalAmount,
      shipping_tier: getShippingTier(isDhaka),
      items: items.map(mapCartItem),
    },
  });
};

// Pushes a GA4 purchase event once an order is successfully placed.
export const pushPurchase = (items, { transactionId, value, shipping, coupon, isDhaka }) => {
  if (typeof window === 'undefined' || !window.dataLayer) return;
  if (!Array.isArray(items) || items.length === 0) return;

  window.dataLayer.push({ ecommerce: null });
  window.dataLayer.push({
    event: 'purchase',
    ecommerce: {
      transaction_id: transactionId,
      affiliation: 'Customer Website',
      currency: CURRENCY,
      value,
      shipping: shipping || 0,
      coupon: coupon || '',
      shipping_tier: getShippingTier(isDhaka),
      items: items.map(mapCartItem),
    },
  });
};

// Pushes a GA4 begin_checkout event on checkout page load.
export const pushBeginCheckout = (items, totalAmount) => {
  if (typeof window === 'undefined' || !window.dataLayer) return;
  if (!Array.isArray(items) || items.length === 0) return;

  window.dataLayer.push({ ecommerce: null });
  window.dataLayer.push({
    event: 'begin_checkout',
    ecommerce: {
      currency: CURRENCY,
      value: totalAmount,
      items: items.map(mapCartItem),
    },
  });
};

// Pushes a GA4 select_item event when a product card is clicked from a list.
export const pushSelectItem = (product, { listId, listName, index = 0 }) => {
  if (typeof window === 'undefined' || !window.dataLayer) return;
  if (!product) return;

  window.dataLayer.push({ ecommerce: null });
  window.dataLayer.push({
    event: 'select_item',
    ecommerce: {
      item_list_id: listId,
      item_list_name: listName,
      items: [{
        item_id: product.id,
        item_name: product.name,
        item_category: product.category || '',
        item_category2: product.subCategory || '',
        item_list_id: listId,
        item_list_name: listName,
        index,
        price: product.price,
        quantity: 1,
      }],
    },
  });
};
